import { useCallback, useState } from 'react';
import clsx from 'clsx';
import CaretDown from 'react-ionicons/lib/CaretDown';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ICartItem,
  useCartContext,
} from 'screens/AccessExpressV3/CartProvider';
import { CartItemList } from '../CartItemList';
import { observer } from 'mobx-react-lite';
import { CheckmarkCircle } from 'react-ionicons';
import {
  Tooltip,
  useTooltip,
} from 'screens/AccessExpressV3/AccessProductDetails/NetworkMonitoringLabel';

const dropdownVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    marginTop: 0,
  },
  visible: {
    opacity: 1,
    height: 'auto',
    // marginTop: '0.5rem',
    transition: {
      height: {
        duration: 0.3,
        ease: 'easeInOut',
      },
      opacity: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    marginTop: 0,
    transition: {
      height: {
        duration: 0.3,
        ease: 'easeInOut',
      },
      opacity: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  },
};

export const UnassignedProducts = observer(() => {
  const [isOpen, setIsOpen] = useState(true);

  const cart = useCartContext();

  const items = cart.unassignedPrimaryMobilityItems;

  const locationId = undefined;

  const handleUndoDelete = (item: ICartItem) => {
    cart.addUnassignedItemBack(item);
  };

  const handleDelete = async (item: ICartItem) => {
    // cart.setLoadingDraftQuote(true);
    if (cart.shippingAssignmentMethod === 'manual' && !item.locationId) {
      await cart.removeMobilityCartItem(String(item.id), true);

      return;
    }

    if (item.category === 'Access') {
      if (item.subtype === 'starlink') {
        await cart.removeStarlinkCartItem(item.item_details!, locationId);
      } else
        await cart.removeAccessCartItem(
          item.item_details?.offering_id ?? '',
          locationId,
        );
    } else if (item.category === 'Mobility') {
      await cart.removeMobilityCartItem(String(item.id));
    }
    // cart.setLoadingDraftQuote(false);
  };
  const handleQuantityChange = (item: ICartItem, newQty: number) => {
    if (item.category === 'Access' && item.subtype === 'starlink') {
      cart.updateStarlinkItemQuantity(item, newQty);
      return;
    }
    cart.updateItemQuantity(item.id, newQty);
  };

  const itemsAreAssigned = cart.unassignedItemsAreAssigned;

  const getVariant = useCallback(
    (item: ICartItem) => {
      return item.item_details?.deleted === true
        ? 'deleted'
        : cart.shippingAssignmentMethod === 'unset'
          ? 'default'
          : itemsAreAssigned
            ? 'assigned'
            : 'unassigned';
    },
    [cart.shippingAssignmentMethod, itemsAreAssigned],
  );

  const { triggerEltRef, isVisible, handleEnter, handleLeave } = useTooltip();

  return (
    <div className="flex flex-col gap-4">
      <div className="h-4">
        {cart.shippingAssignmentMethod === 'manual' ? (
          <div className="relative w-full overflow-hidden text-sm/[24px] font-bold">
            <div
              className={clsx(
                'flex w-[200%] transition-transform duration-500 ease-in-out',
                !itemsAreAssigned ? 'translate-x-0' : '-translate-x-1/2',
              )}
            >
              <div
                className={clsx(
                  'w-[100%] truncate duration-500 ease-in-out',
                  itemsAreAssigned && 'opacity-0',
                )}
              >
                Add this item to at least one of the above carts
              </div>
              <div className="flex w-[100%] justify-end gap-2 truncate text-right text-content-base-default">
                Added to cart!
                <CheckmarkCircle
                  color="currentColor"
                  width="24px"
                  height="24px"
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className="flex cursor-pointer flex-row items-center gap-4"
            onClick={() => setIsOpen(!isOpen)}
            ref={triggerEltRef}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          >
            <div className="flex-1 border-b border-b-stroke-base-subdued"></div>
            <div className="flex flex-none flex-row gap-2">
              <span className="text-xs font-bold">
                Location assignment required
              </span>
              <span className={clsx(isOpen ? 'rotate-180' : 'rotate-0')}>
                <CaretDown color="currentColor" width="16px" height="16px" />
              </span>
            </div>
            <div className="flex-1 border-b border-b-stroke-base-subdued"></div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={dropdownVariants}
            className="vertical-scrollbar flex max-h-[20vh] flex-grow flex-col overflow-y-auto overflow-x-hidden !scrollbar-track-transparent scrollbar-thumb-stroke-base-subdued"
          >
            <CartItemList
              items={items}
              className="!gap-2"
              // not used in unassigned products list
              // onEdit={handleEdit}
              onDelete={handleDelete}
              onQuantityChange={handleQuantityChange}
              variant={getVariant}
              onUndoDelete={handleUndoDelete}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <Tooltip
        triggerEltRef={triggerEltRef}
        isVisible={isVisible}
        body={
          'This item will need to be associated with an address during checkout.'
        }
        placement="top"
        arrow
        tooltipOffset={24}
      />
    </div>
  );
});
